import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box } from "@mui/material";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import DownloadIcon from "@mui/icons-material/Download";
import { downloadSingleFile } from "../../services/FileDownloader";

const buttonStyle = {
  fontSize: 18,
  padding: "6px 12px",
  borderRadius: "8px",
  backgroundColor: "rgba(200, 200, 200, 0.25)",
  color: "white",
  border: "none",
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

interface DraggableImageContainerProps {
  src: string;
  alt?: string;
}

const DraggableImageContainer: React.FC<DraggableImageContainerProps> = ({ src, alt = "Image" }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const [startPos, setStartPos] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(0.25);

  const isVideo = useMemo(() => {
    const videoExtensions = [".mp4", ".webm", ".ogg"];
    return videoExtensions.some((ext) => src.toLowerCase().includes(ext));
  }, [src]);

  useEffect(() => {
    const handleMouseUpGlobal = () => setDragging(false);
    window.addEventListener("mouseup", handleMouseUpGlobal);
    return () => window.removeEventListener("mouseup", handleMouseUpGlobal);
  }, []);

  useEffect(() => {
    resetView();
  }, [src]);

  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    setDragging(true);
    setStartPos({ x: e.clientX - position.x, y: e.clientY - position.y });
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!dragging) return;
    setPosition({ x: e.clientX - startPos.x, y: e.clientY - startPos.y });
  };

  const handleWheel = (e: React.WheelEvent) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? -0.1 : 0.1;
    setZoom((prev) => Math.min(Math.max(prev + delta, 0.25), 5));
  };

  const handleZoomIn = () => {
    setZoom((prev) => Math.min(prev + 0.1, 5));
  };

  const handleZoomOut = () => {
    setZoom((prev) => Math.max(prev - 0.1, 0.25));
  };

  const resetView = () => {
    setZoom(0.25);
    setPosition({ x: 0, y: 0 });
  };

  return (
    <Box
      ref={containerRef}
      onMouseDown={isVideo ? undefined : handleMouseDown}
      onMouseMove={isVideo ? undefined : handleMouseMove}
      onWheel={isVideo ? undefined : handleWheel}
      sx={{
        width: "100%",
        height: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        position: "relative",
        overflow: "hidden",
        cursor: dragging ? "grabbing" : isVideo ? "default" : "grab",
      }}
    >
      <Box
        sx={{
          transform: `translate(${position.x}px, ${position.y}px) scale(${zoom})`,
          transition: dragging ? "none" : "transform 0.2s ease-out",
          maxWidth: "none",
          maxHeight: "none",
          userSelect: "none",
          pointerEvents: "none",
        }}
      >
        {isVideo ? (
          <video
            src={src}
            style={{
              display: "block",
              maxWidth: "none",
              maxHeight: "none",
              userSelect: "none",
              pointerEvents: "none",
            }}
          />
        ) : (
          <img
            src={src}
            alt={alt}
            style={{
              display: "block",
              maxWidth: "none",
              maxHeight: "none",
              userSelect: "none",
              pointerEvents: "none",
            }}
            draggable={false}
          />
        )}
      </Box>

      <Box
        sx={{
          position: "absolute",
          bottom: 16,
          display: "flex",
          justifyContent: "center",
          width: "100%",
          gap: 1,
          zIndex: 1,
        }}
      >
        <button onClick={handleZoomOut} style={buttonStyle}>-</button>
        {!isVideo && (
          <button onClick={() => downloadSingleFile(src)} style={buttonStyle}>
            <DownloadIcon />
          </button>
        )}
        <button onClick={resetView} style={buttonStyle}><RestartAltIcon /></button>
        <button onClick={handleZoomIn} style={buttonStyle}>+</button>
      </Box>

    </Box>
  );
};

export default DraggableImageContainer;
